import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Project, Status, Visibility } from '../interfaces/project';
import { Observable, map, switchMap } from 'rxjs';
import { PortfolioProjectService } from '../portfolio-project.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getProjectImageUrls, PROFILE_AVATAR_FALLBACK } from '../utils/project-images';
import { ProjectImageCarouselComponent } from './project-image-carousel/project-image-carousel.component';
import { UserService } from '../services/user-service/user.service';

@Component({
  selector: 'app-project',
  imports: [
    CommonModule,
    RouterLink,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ProjectImageCarouselComponent
  ],
  templateUrl: './project.component.html',
  styleUrl: './project.component.sass'
})
export class ProjectComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly portfolioService = inject(PortfolioProjectService);
  private readonly userService = inject(UserService);

  /** Project plus merged image list for the carousel (stable between Firestore emits). */
  projectView$: Observable<{ project: Project; carouselImages: string[] } | undefined> | undefined;

  readonly isAdmin$ = this.userService.isAdmin$;

  readonly Status = Status;
  readonly Visibility = Visibility;
  readonly profileAvatarSrc = PROFILE_AVATAR_FALLBACK;

  ngOnInit(): void {
    const id$ = this.route.paramMap.pipe(
      switchMap((param) => this.portfolioService.getProjectById(param.get('id') ?? ''))
    );
    this.projectView$ = id$.pipe(
      map((project) =>
        project ? { project, carouselImages: getProjectImageUrls(project) } : undefined
      )
    );
  }

  editProject(projectId: string): void {
    void this.router.navigate(['/admin/projects', projectId, 'edit']);
  }

  formatDateRange(project: Project): string {
    const start = project.startDate ? this.formatDate(project.startDate) : null;
    const end = project.endDate ? this.formatDate(project.endDate) : null;
    if (start && end) {
      return `${start} — ${end}`;
    }
    if (start) {
      return `From ${start}`;
    }
    if (end) {
      return `Until ${end}`;
    }
    return '';
  }

  private formatDate(d: Date): string {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
}
